import React, { useEffect, useRef } from "react";
import "./styles.scss";

const NON_SORTABLE_HEADERS = new Set([
  "",
  "AÇÃO",
  "AÇÕES",
  "OPÇÃO",
  "OPÇÕES",
  "MAPA",
]);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const sortableValue = (cell) => {
  const text = normalizeText(cell?.textContent);
  const dateMatch = text.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/,
  );
  if (dateMatch) {
    return new Date(
      Number(dateMatch[3]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[1]),
      Number(dateMatch[4] || 0),
      Number(dateMatch[5] || 0),
    ).getTime();
  }

  const numeric = text
    .replace(/[^\d,.-]/g, "")
    .replace(/\.(?=\d{3}(?:\D|$))/g, "")
    .replace(",", ".");
  if (numeric && /^-?\d+(?:\.\d+)?$/.test(numeric)) return Number(numeric);

  return text;
};

const compareValues = (first, second) => {
  if (typeof first === "number" && typeof second === "number") {
    return first - second;
  }
  return String(first).localeCompare(String(second), "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
};

const getHeaderCells = (table) => {
  const rows = table.tHead?.rows;
  return rows?.length ? Array.from(rows[rows.length - 1].cells) : [];
};

const isManagedHeader = (header) =>
  header.dataset.gridSortManaged === "true" ||
  Boolean(header.querySelector(".MuiTableSortLabel-root"));

const isSortableHeader = (header) => {
  const label = normalizeText(header.textContent).toLocaleUpperCase("pt-BR");
  return (
    !NON_SORTABLE_HEADERS.has(label) &&
    !header.hasAttribute("data-grid-sort-disabled") &&
    !header.querySelector("button, input, select")
  );
};

const standardizeTable = (table) => {
  const headers = getHeaderCells(table);
  const emailIndexes = new Set();

  headers.forEach((header, index) => {
    const label = normalizeText(header.textContent).toLocaleUpperCase("pt-BR");
    if (label.includes("EMAIL") || label.includes("E-MAIL")) {
      emailIndexes.add(index);
    }
    if (!isManagedHeader(header) && isSortableHeader(header)) {
      header.classList.add("xavier-grid-sortable-header");
      header.tabIndex = 0;
      header.setAttribute("role", "button");
      header.setAttribute("title", "Clique para ordenar");
    }
  });

  Array.from(table.tBodies || []).forEach((body) => {
    Array.from(body.rows).forEach((row) => {
      Array.from(row.cells).forEach((cell, index) => {
        const content = normalizeText(cell.textContent);
        const isEmail = emailIndexes.has(index) || EMAIL_PATTERN.test(content);
        cell.classList.toggle("xavier-grid-email", isEmail);
      });
    });
  });
};

const standardizeEmails = (root) => {
  root.querySelectorAll("input, textarea").forEach((field) => {
    const identifier = `${field.type || ""} ${field.name || ""} ${
      field.id || ""
    } ${field.placeholder || ""}`.toLocaleLowerCase("pt-BR");
    const isEmail =
      identifier.includes("email") ||
      EMAIL_PATTERN.test(normalizeText(field.value));
    field.classList.toggle("xavier-grid-email", isEmail);
  });

  root.querySelectorAll("a, span, p, small, strong, td").forEach((element) => {
    if (element.children.length) return;
    element.classList.toggle(
      "xavier-grid-email",
      EMAIL_PATTERN.test(normalizeText(element.textContent)),
    );
  });
};

const sortTable = (header) => {
  const table = header.closest("table");
  if (!table || isManagedHeader(header) || !isSortableHeader(header)) return;

  const columnIndex = header.cellIndex;
  const currentDirection = header.dataset.sortDirection;
  const direction = currentDirection === "asc" ? "desc" : "asc";

  getHeaderCells(table).forEach((cell) => {
    cell.removeAttribute("data-sort-direction");
    cell.removeAttribute("aria-sort");
  });
  header.dataset.sortDirection = direction;
  header.setAttribute(
    "aria-sort",
    direction === "asc" ? "ascending" : "descending",
  );

  Array.from(table.tBodies || []).forEach((body) => {
    const sortableRows = Array.from(body.rows).filter(
      (row) => row.cells.length > columnIndex && row.cells.length > 1,
    );
    sortableRows
      .map((row, originalIndex) => ({
        row,
        originalIndex,
        value: sortableValue(row.cells[columnIndex]),
      }))
      .sort((first, second) => {
        const comparison = compareValues(first.value, second.value);
        const stableComparison =
          comparison || first.originalIndex - second.originalIndex;
        return direction === "desc" ? -stableComparison : stableComparison;
      })
      .forEach(({ row }) => body.appendChild(row));
  });
};

export const GridStandards = ({ children }) => {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const refresh = () => {
      root
        .querySelectorAll("table")
        .forEach((table) => standardizeTable(table));
      standardizeEmails(root);
    };
    const Observer = window.MutationObserver;
    const observer = Observer ? new Observer(refresh) : null;
    if (observer) observer.observe(root, { childList: true, subtree: true });
    refresh();

    const activateSort = (event) => {
      const header = event.target.closest("th");
      if (!header || !root.contains(header)) return;
      if (event.type === "keydown" && !["Enter", " "].includes(event.key))
        return;
      if (event.type === "keydown") event.preventDefault();
      sortTable(header);
    };

    root.addEventListener("click", activateSort);
    root.addEventListener("keydown", activateSort);
    return () => {
      if (observer) observer.disconnect();
      root.removeEventListener("click", activateSort);
      root.removeEventListener("keydown", activateSort);
    };
  }, []);

  return (
    <div className="xavier-grid-standard" ref={rootRef}>
      {children}
    </div>
  );
};
